import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { addDays, format, startOfWeek } from 'date-fns';
import { AdminNav } from '../AdminNav';
import { apiUrl } from '../../config';
import {
  AdminCreateAppointmentBookingRequest,
  AppointmentBookingDto,
  AppointmentEvaluationLinkDto,
  AdminManualBookingCustomerLookupDto,
  AdminManualBookingSvvLookupDto,
  AppointmentStatus,
  CancelAppointmentBookingRequest,
  JOB_TYPE_LABELS,
  JOB_TYPE_OPTIONS,
  JobType,
  AppointmentAvailabilityRuleDto,
  jobTypeLabel,
  jobTypeRequiresNote,
  noteRequiredMessage,
} from '../../interfaces/appointments';
import { Link as RouterLink } from 'react-router-dom';
import { computeCalendarHourRange } from './calendarRange';
import { bookingCountColor, countNonHotelBookings } from './bookingCount';

const SLOT_MINUTES = 15;

type DragState = {
  date: string;
  startSlot: number;
  currentSlot: number;
};

type TimeSelection = {
  date: string;
  startTime: string;
  durationMinutes: number;
};

type DragPointer = {
  x: number;
  y: number;
};

const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

const timeToMinutes = (timeValue: string): number => {
  const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
  return (hours * 60) + minutes;
};

const slotStartMinutes = (slotIndex: number, startHour: number): number =>
  (startHour * 60) + (slotIndex * SLOT_MINUTES);

const slotIndexToTime = (slotIndex: number, startHour: number): string => {
  const minutes = slotStartMinutes(slotIndex, startHour);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const sortAppointments = (items: AppointmentBookingDto[]): AppointmentBookingDto[] =>
  [...items].sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));

const formatVehicleSummary = (vehicle: AdminManualBookingSvvLookupDto | null): string => {
  if (!vehicle) {
    return '';
  }

  return [vehicle.brand?.trim(), vehicle.model?.trim()]
    .filter((value): value is string => Boolean(value))
    .join(' ');
};

const appendVehicleSummaryToNote = (note: string | undefined, vehicleSummary: string): string | undefined => {
  const normalizedNote = note?.trim() ?? '';
  if (!vehicleSummary) {
    return normalizedNote || undefined;
  }

  if (!normalizedNote) {
    return vehicleSummary;
  }

  if (normalizedNote.toLowerCase().includes(vehicleSummary.toLowerCase())) {
    return normalizedNote;
  }

  return `${normalizedNote} | ${vehicleSummary}`;
};

const createEmptyManualForm = (): AdminCreateAppointmentBookingRequest => ({
  customerName: '',
  phone: '',
  registrationNumber: '',
  date: '',
  startTime: '',
  durationMinutes: 30,
  note: '',
  jobType: '',
  unknownCustomer: false,
  createdBy: 'Admin',
});

const createFormFromBooking = (booking: AppointmentBookingDto): AdminCreateAppointmentBookingRequest => ({
  customerName: booking.customerName ?? '',
  phone: booking.customerPhone ?? '',
  registrationNumber: booking.registrationNumber ?? '',
  date: booking.date ?? '',
  startTime: booking.time ?? '',
  durationMinutes: booking.durationMinutes,
  note: booking.customerNote ?? '',
  jobType: jobTypeLabel(booking.jobType) ? (booking.jobType as JobType) : '',
  unknownCustomer: booking.isUnknownCustomer ?? false,
  createdBy: 'Admin',
});

function AdminManualBooking() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [weekStart, setWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [appointments, setAppointments] = useState<AppointmentBookingDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragPointer, setDragPointer] = useState<DragPointer | null>(null);
  const [selection, setSelection] = useState<TimeSelection | null>(null);
  const [manualForm, setManualForm] = useState<AdminCreateAppointmentBookingRequest>(createEmptyManualForm);
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [svvLookupLoading, setSvvLookupLoading] = useState(false);
  const [svvVehicle, setSvvVehicle] = useState<AdminManualBookingSvvLookupDto | null>(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<AdminCreateAppointmentBookingRequest>(createEmptyManualForm);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [dialogCancelling, setDialogCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [evaluationMenuAnchorEl, setEvaluationMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [evaluationMenuRegistration, setEvaluationMenuRegistration] = useState<string | null>(null);
  const [evaluationLinksByRegistration, setEvaluationLinksByRegistration] = useState<Record<string, AppointmentEvaluationLinkDto[]>>({});
  const [loadingEvaluationRegistration, setLoadingEvaluationRegistration] = useState<string | null>(null);
  const [evaluationMenuError, setEvaluationMenuError] = useState<string | null>(null);
  const initializedDialogBookingIdRef = React.useRef<number | null>(null);
  const requestedEvaluationRegistrationsRef = React.useRef<Set<string>>(new Set());

  const weekDays = useMemo(
    () => Array.from({ length: 5 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );

  const [availabilityRules, setAvailabilityRules] = useState<AppointmentAvailabilityRuleDto[]>([]);

  useEffect(() => {
    // The enabled (open) period widens the calendar. Failing to load it just keeps the default range.
    axios.get<AppointmentAvailabilityRuleDto[]>(`${apiUrl}/admin/appointments/rules`)
      .then((response) => setAvailabilityRules(Array.isArray(response?.data) ? response.data : []))
      .catch(() => setAvailabilityRules([]));
  }, []);

  const { startHour: calendarStartHour, endHour: calendarEndHour } = useMemo(
    () => computeCalendarHourRange(weekDays.map((day) => toIsoDate(day)), appointments, availabilityRules),
    [weekDays, appointments, availabilityRules],
  );
  const slotCount = ((calendarEndHour - calendarStartHour) * 60) / SLOT_MINUTES;

  const weekLabel = `${format(weekDays[0], 'dd.MM.yyyy')} - ${format(weekDays[4], 'dd.MM.yyyy')}`;
  const activeBooking = appointments.find((item) => item.id === activeBookingId) ?? null;
  const slotHeight = isSmallScreen ? 16 : (isMediumScreen ? 20 : 24);
  const timeColumnWidth = isSmallScreen ? 56 : (isMediumScreen ? 68 : 80);
  const dayColumnMinWidth = isSmallScreen ? 96 : (isMediumScreen ? 114 : 130);
  const dragDurationMinutes = dragState
    ? ((Math.abs(dragState.currentSlot - dragState.startSlot) + 1) * SLOT_MINUTES)
    : null;
  const vehicleSummary = formatVehicleSummary(svvVehicle);
  const calendarBookingLabel = (booking: AppointmentBookingDto): string => {
    const note = booking.customerNote?.trim();
    const job = jobTypeLabel(booking.jobType);
    if (note && booking.durationMinutes >= 45) {
      return `${job ? `${job}: ` : ''}${booking.customerName}: ${note}`;
    }

    const base = `${booking.customerName} (${booking.durationMinutes}m)`;
    return job && booking.durationMinutes >= 30 ? `${job} - ${base}` : base;
  };
  const isEvaluationMenuOpen = Boolean(evaluationMenuAnchorEl);
  const evaluationLinks = evaluationMenuRegistration
    ? (evaluationLinksByRegistration[evaluationMenuRegistration] ?? [])
    : [];
  const evaluationLinksWithId = evaluationLinks.filter((evaluation) => evaluation.id !== null);

  const bookingCoveringSlot = useCallback((dateIso: string, slotIndex: number): AppointmentBookingDto | null => {
    const slotStart = slotStartMinutes(slotIndex, calendarStartHour);
    const slotEnd = slotStart + SLOT_MINUTES;

    return appointments.find((appointment) => {
      if (appointment.status === AppointmentStatus.CancelledByAdmin || appointment.date !== dateIso) {
        return false;
      }

      const bookingStart = timeToMinutes(appointment.time);
      const bookingEnd = bookingStart + appointment.durationMinutes;
      return bookingStart < slotEnd && slotStart < bookingEnd;
    }) ?? null;
  }, [appointments, calendarStartHour]);

  const isSlotBooked = useCallback((dateIso: string, slotIndex: number): boolean =>
    bookingCoveringSlot(dateIso, slotIndex) !== null, [bookingCoveringSlot]);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const from = toIsoDate(weekStart);
      const to = toIsoDate(addDays(weekStart, 6));

      const response = await axios.get<AppointmentBookingDto[]>(
        `${apiUrl}/admin/appointments/range/${from}/${to}`,
        { params: { includeCancelled: false } },
      );

      setAppointments(sortAppointments(response.data));
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke hente timebestillinger for valgt uke.');
      }
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  const loadEvaluationLinks = useCallback(async (registrationNumber: string) => {
    // Each registration is requested once. Without this, every response changes the cache state and
    // re-runs the effect below, which re-requests all registrations still in flight.
    if (requestedEvaluationRegistrationsRef.current.has(registrationNumber)) {
      return;
    }
    requestedEvaluationRegistrationsRef.current.add(registrationNumber);

    setLoadingEvaluationRegistration(registrationNumber);
    setEvaluationMenuError(null);

    try {
      const response = await axios.get<AppointmentEvaluationLinkDto[]>(
        `${apiUrl}/admin/evaluations/vehicle/${registrationNumber}`,
      );
      setEvaluationLinksByRegistration((prev) => ({ ...prev, [registrationNumber]: response.data }));
    } catch {
      setEvaluationMenuError('Kunne ikke hente dekkvurderinger for kjøretøyet.');
    } finally {
      setLoadingEvaluationRegistration(null);
    }
  }, []);

  useEffect(() => {
    void loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    const uniqueRegistrations = Array.from(
      new Set(
        appointments
          .map((appointment) => appointment.registrationNumber?.trim().toUpperCase())
          .filter((value): value is string => Boolean(value)),
      ),
    );

    uniqueRegistrations.forEach((registrationNumber) => {
      void loadEvaluationLinks(registrationNumber);
    });
  }, [appointments, loadEvaluationLinks]);

  useEffect(() => {
    if (!bookingDialogOpen) {
      initializedDialogBookingIdRef.current = null;
      return;
    }

    if (!activeBooking || activeBookingId === null) {
      return;
    }

    if (initializedDialogBookingIdRef.current === activeBookingId) {
      return;
    }

    setEditForm(createFormFromBooking(activeBooking));
    initializedDialogBookingIdRef.current = activeBookingId;
  }, [activeBooking, activeBookingId, bookingDialogOpen]);

  useEffect(() => {
    if (loading || !bookingDialogOpen || activeBookingId === null) {
      return;
    }

    if (!activeBooking) {
      setBookingDialogOpen(false);
      setActiveBookingId(null);
      setCancelReason('');
      initializedDialogBookingIdRef.current = null;
    }
  }, [activeBooking, activeBookingId, bookingDialogOpen, loading]);

  const isSlotWithinSelection = (dateIso: string, slotIndex: number): boolean => {
    if (dragState && dragState.date === dateIso) {
      const start = Math.min(dragState.startSlot, dragState.currentSlot);
      const endExclusive = Math.max(dragState.startSlot, dragState.currentSlot) + 1;
      return slotIndex >= start && slotIndex < endExclusive;
    }

    if (selection && selection.date === dateIso) {
      const start = Math.floor((timeToMinutes(selection.startTime) - (calendarStartHour * 60)) / SLOT_MINUTES);
      const endExclusive = start + (selection.durationMinutes / SLOT_MINUTES);
      return slotIndex >= start && slotIndex < endExclusive;
    }

    return false;
  };

  const finalizeDrag = useCallback(() => {
    if (!dragState) {
      return;
    }

    const startSlot = Math.min(dragState.startSlot, dragState.currentSlot);
    const endExclusive = Math.max(dragState.startSlot, dragState.currentSlot) + 1;
    const durationMinutes = (endExclusive - startSlot) * SLOT_MINUTES;

    setDragState(null);
    setDragPointer(null);

    for (let slot = startSlot; slot < endExclusive; slot += 1) {
      if (isSlotBooked(dragState.date, slot)) {
        setSelection(null);
        setSelectionError('Valgt tidsrom overlapper med en eksisterende booking.');
        return;
      }
    }

    const nextSelection = {
      date: dragState.date,
      startTime: slotIndexToTime(startSlot, calendarStartHour),
      durationMinutes,
    };

    setSelection(nextSelection);
    setSelectionError(null);
    setManualForm((prev) => ({
      ...prev,
      date: nextSelection.date,
      startTime: nextSelection.startTime,
      durationMinutes: nextSelection.durationMinutes,
    }));
  }, [dragState, isSlotBooked, calendarStartHour]);

  useEffect(() => {
    const handleMouseUp = () => finalizeDrag();
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [finalizeDrag]);

  const handleCellMouseDown = (
    event: React.MouseEvent<HTMLElement>,
    dateIso: string,
    slotIndex: number,
  ) => {
    if (isSlotBooked(dateIso, slotIndex)) {
      return;
    }

    setError(null);
    setSuccess(null);
    setSelectionError(null);
    setDragState({
      date: dateIso,
      startSlot: slotIndex,
      currentSlot: slotIndex,
    });
    setDragPointer({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleCellMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    dateIso: string,
    slotIndex: number,
  ) => {
    setDragState((current) => {
      if (!current || current.date !== dateIso) {
        return current;
      }

      return {
        ...current,
        currentSlot: slotIndex,
      };
    });

    if (dragState?.date === dateIso) {
      setDragPointer({
        x: event.clientX,
        y: event.clientY,
      });
    }
  };

  const lookupCustomerByRegistration = useCallback(async (registrationNumber?: string) => {
    const normalizedRegistration = (registrationNumber ?? manualForm.registrationNumber).trim().toUpperCase();
    if (!normalizedRegistration) {
      setManualForm((prev) => ({
        ...prev,
        customerName: '',
        phone: '',
      }));
      return;
    }

    setCustomerLookupLoading(true);

    try {
      const response = await axios.get<AdminManualBookingCustomerLookupDto>(
        `${apiUrl}/admin/appointments/manual-bookings/customer/${encodeURIComponent(normalizedRegistration)}`,
      );

      setManualForm((prev) => ({
        ...prev,
        registrationNumber: normalizedRegistration,
        customerName: response.data.customerName ?? '',
        phone: response.data.phone ?? '',
      }));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setManualForm((prev) => (prev.unknownCustomer
          ? { ...prev, registrationNumber: normalizedRegistration }
          : {
            ...prev,
            registrationNumber: normalizedRegistration,
            customerName: '',
            phone: '',
          }));
      } else if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke hente kundeinformasjon for registreringsnummer.');
      }
    } finally {
      setCustomerLookupLoading(false);
    }
  }, [manualForm.registrationNumber]);

  const lookupVehicleByRegistration = useCallback(async (registrationNumber?: string) => {
    const normalizedRegistration = (registrationNumber ?? manualForm.registrationNumber).trim().toUpperCase();
    if (!normalizedRegistration) {
      setSvvVehicle(null);
      return;
    }

    setSvvLookupLoading(true);

    try {
      const response = await axios.get<AdminManualBookingSvvLookupDto>(
        `${apiUrl}/admin/svvdata/${encodeURIComponent(normalizedRegistration)}`,
      );

      const nextVehicle: AdminManualBookingSvvLookupDto = {
        brand: response.data.brand?.trim() ?? '',
        model: response.data.model?.trim() ?? '',
      };

      setManualForm((prev) => ({
        ...prev,
        registrationNumber: normalizedRegistration,
      }));
      setSvvVehicle(nextVehicle);
    } catch (err: any) {
      setSvvVehicle(null);
      if (err?.response?.data) {
        setError(String(err.response.data));
      }
    } finally {
      setSvvLookupLoading(false);
    }
  }, [manualForm.registrationNumber]);

  const triggerRegistrationLookups = useCallback(() => {
    const normalizedRegistration = manualForm.registrationNumber.trim().toUpperCase();
    if (!normalizedRegistration) {
      setSvvVehicle(null);
      // Keep what the admin typed when the customer is unknown: the fields are optional, not derived.
      setManualForm((prev) => (prev.unknownCustomer ? prev : {
        ...prev,
        customerName: '',
        phone: '',
      }));
      return;
    }

    void lookupCustomerByRegistration(normalizedRegistration);
    void lookupVehicleByRegistration(normalizedRegistration);
  }, [lookupCustomerByRegistration, lookupVehicleByRegistration, manualForm.registrationNumber]);

  const handleUnknownCustomerChange = (checked: boolean) => {
    setManualForm((prev) => ({
      ...prev,
      unknownCustomer: checked,
    }));
  };

  const submitManualBooking = async () => {
    setError(null);
    setSuccess(null);

    if (!manualForm.date || !manualForm.startTime) {
      setSelectionError('Velg tidspunkt ved å klikke og dra i kalenderen.');
      return;
    }

    if (!manualForm.unknownCustomer && (
      !manualForm.customerName.trim() ||
      !manualForm.phone.trim() ||
      !manualForm.registrationNumber.trim())) {
      setError('Fyll ut kundenavn, telefonnummer og registreringsnummer.');
      return;
    }

    if (!manualForm.jobType) {
      setError('Velg jobbtype.');
      return;
    }

    if (jobTypeRequiresNote(manualForm.jobType) && !manualForm.note?.trim()) {
      setError(noteRequiredMessage(manualForm.jobType));
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${apiUrl}/admin/appointments/manual-bookings`, {
        ...manualForm,
        customerName: manualForm.customerName.trim(),
        phone: manualForm.phone.trim(),
        registrationNumber: manualForm.registrationNumber.trim().toUpperCase(),
        note: appendVehicleSummaryToNote(manualForm.note, vehicleSummary),
      });

      setSuccess('Manuell booking opprettet og bekreftet.');
      setSelection(null);
      setManualForm(createEmptyManualForm());
      setSvvVehicle(null);
      await loadAppointments();
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke opprette manuell booking.');
      }
    } finally {
      setSaving(false);
    }
  };

  const openBookingDialog = (booking: AppointmentBookingDto) => {
    setActiveBookingId(booking.id);
    setEditForm(createFormFromBooking(booking));
    setCancelReason('');
    setBookingDialogOpen(true);
  };

  const closeBookingDialog = () => {
    setBookingDialogOpen(false);
    setActiveBookingId(null);
    setDialogSaving(false);
    setDialogCancelling(false);
    setCancelReason('');
    initializedDialogBookingIdRef.current = null;
  };

  const submitEditedBooking = async () => {
    if (!activeBookingId) {
      return;
    }

    if (!editForm.unknownCustomer && (
      !editForm.customerName.trim() ||
      !editForm.phone.trim() ||
      !editForm.registrationNumber.trim())) {
      setError('Fyll ut kundenavn, telefonnummer og registreringsnummer.');
      return;
    }

    if (jobTypeRequiresNote(editForm.jobType) && !editForm.note?.trim()) {
      setError(noteRequiredMessage(editForm.jobType));
      return;
    }

    setDialogSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.put(`${apiUrl}/admin/appointments/${activeBookingId}`, {
        ...editForm,
        jobType: editForm.jobType || undefined,
        customerName: editForm.customerName.trim(),
        phone: editForm.phone.trim(),
        registrationNumber: editForm.registrationNumber.trim().toUpperCase(),
        note: editForm.note?.trim() || undefined,
        createdBy: 'Admin',
      });

      setSuccess('Booking oppdatert.');
      closeBookingDialog();
      await loadAppointments();
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke oppdatere booking.');
      }
    } finally {
      setDialogSaving(false);
    }
  };

  const submitCancellation = async () => {
    if (!activeBookingId) {
      return;
    }

    setDialogCancelling(true);
    setError(null);
    setSuccess(null);

    const payload: CancelAppointmentBookingRequest = {
      reason: cancelReason.trim() || undefined,
      cancelledBy: 'Admin',
    };

    try {
      await axios.patch(`${apiUrl}/admin/appointments/${activeBookingId}/cancel`, payload);
      setSuccess('Booking avlyst.');
      closeBookingDialog();
      await loadAppointments();
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke avlyse booking.');
      }
    } finally {
      setDialogCancelling(false);
    }
  };

  const openEvaluationMenu = (event: React.MouseEvent<HTMLElement>, registrationNumber: string) => {
    event.stopPropagation();
    const normalizedRegistrationNumber = registrationNumber.trim().toUpperCase();
    setEvaluationMenuAnchorEl(event.currentTarget);
    setEvaluationMenuRegistration(normalizedRegistrationNumber);
    setEvaluationMenuError(null);
    void loadEvaluationLinks(normalizedRegistrationNumber);
  };

  const closeEvaluationMenu = () => {
    setEvaluationMenuAnchorEl(null);
    setEvaluationMenuRegistration(null);
    setEvaluationMenuError(null);
  };

  return (
    <Grid container>
      <Grid item xs={12} md={2}>
        <AdminNav />
      </Grid>
      <Grid item xs={12} md={10} sx={{ p: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Manuell booking
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Velg tidspunkt i ukevisningen ved å klikke og dra, og registrer kunden mens du er i telefonen.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {selectionError && <Alert severity="warning" sx={{ mb: 2 }}>{selectionError}</Alert>}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kundeinformasjon
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualForm.unknownCustomer}
                    onChange={(event) => handleUnknownCustomerChange(event.target.checked)}
                  />
                }
                label="Ukjent kunde (kundenavn, telefonnummer og registreringsnummer er valgfritt)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Registreringsnummer"
                value={manualForm.registrationNumber}
                onChange={(event) => {
                  setSvvVehicle(null);
                  setManualForm((prev) => ({ ...prev, registrationNumber: event.target.value }));
                }}
                onBlur={triggerRegistrationLookups}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Kundenavn"
                value={manualForm.customerName}
                onChange={(event) => setManualForm((prev) => ({ ...prev, customerName: event.target.value }))}
                InputProps={{
                  endAdornment: customerLookupLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={14} />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Telefonnummer"
                value={manualForm.phone}
                onChange={(event) => setManualForm((prev) => ({ ...prev, phone: event.target.value }))}
                InputProps={{
                  endAdornment: customerLookupLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={14} />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Valgt tidspunkt"
                value={
                  manualForm.date && manualForm.startTime
                    ? `${format(new Date(`${manualForm.date}T00:00:00`), 'dd.MM.yyyy')} ${manualForm.startTime} (${manualForm.durationMinutes} min)`
                    : ''
                }
                placeholder="Velg i kalenderen under"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                label="Jobbtype"
                value={manualForm.jobType ?? ''}
                onChange={(event) => setManualForm((prev) => ({ ...prev, jobType: event.target.value as JobType | '' }))}
              >
                {JOB_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{JOB_TYPE_LABELS[option]}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                required={jobTypeRequiresNote(manualForm.jobType)}
                error={jobTypeRequiresNote(manualForm.jobType) && !manualForm.note?.trim()}
                helperText={jobTypeRequiresNote(manualForm.jobType) && !manualForm.note?.trim() ? noteRequiredMessage(manualForm.jobType) : undefined}
                label="Hva skal gjøres"
                value={manualForm.note}
                onChange={(event) => setManualForm((prev) => ({ ...prev, note: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="contained" onClick={submitManualBooking} disabled={saving}>
                  {saving ? 'Lagrer...' : 'Opprett manuell booking'}
                </Button>
                {vehicleSummary && (
                  <Typography variant="body2" color="text.secondary">
                    Kjøretøy: {vehicleSummary}
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, bgcolor: '#0b1220', border: '1px solid #1e293b', position: 'relative' }}>
          {dragState && dragDurationMinutes && dragPointer && (
            <Box
              sx={{
                position: 'fixed',
                left: `${dragPointer.x + 14}px`,
                top: `${dragPointer.y + 14}px`,
                zIndex: 1400,
                bgcolor: '#111827',
                color: '#e2e8f0',
                border: '1px solid #38bdf8',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                fontSize: 12,
                fontWeight: 700,
                pointerEvents: 'none',
                boxShadow: '0 8px 24px rgba(2, 6, 23, 0.45)',
              }}
            >
              {dragDurationMinutes} min
            </Box>
          )}

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setWeekStart((prev) => addDays(prev, -7))}
              sx={{ borderColor: '#334155', color: '#cbd5e1' }}
            >
              Forrige uke
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
              sx={{ borderColor: '#334155', color: '#cbd5e1' }}
            >
              Denne uke
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setWeekStart((prev) => addDays(prev, 7))}
              sx={{ borderColor: '#334155', color: '#cbd5e1' }}
            >
              Neste uke
            </Button>
            <Typography variant="body2" sx={{ color: '#93c5fd' }}>
              {weekLabel}
            </Typography>
            {loading && <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Laster...</Typography>}
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: timeColumnWidth,
                      color: '#94a3b8',
                      borderColor: '#1e293b',
                      bgcolor: '#111827',
                      py: 0.5,
                    }}
                  >
                    Tid
                  </TableCell>
                  {weekDays.map((day) => (
                    <TableCell
                      key={toIsoDate(day)}
                      sx={{
                        minWidth: dayColumnMinWidth,
                        color: '#cbd5e1',
                        borderColor: '#1e293b',
                        bgcolor: '#111827',
                        py: 0.5,
                      }}
                    >
                      <Box
                        component="span"
                        data-testid={`booking-count-${toIsoDate(day)}`}
                        sx={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: bookingCountColor(countNonHotelBookings(appointments, toIsoDate(day))) }}
                      >
                        {`Ikke hotell: ${countNonHotelBookings(appointments, toIsoDate(day))}`}
                      </Box>
                      {format(day, 'EEE dd.MM')}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: slotCount }, (_, slotIndex) => (
                  <TableRow key={slotIndex}>
                    <TableCell
                      sx={{
                        color: '#64748b',
                        borderColor: '#1e293b',
                        bgcolor: '#0f172a',
                        height: slotHeight,
                        minHeight: slotHeight,
                        maxHeight: slotHeight,
                        py: 0,
                        lineHeight: `${slotHeight}px`,
                      }}
                    >
                      {slotIndex % 4 === 0 ? slotIndexToTime(slotIndex, calendarStartHour) : ''}
                    </TableCell>
                    {weekDays.map((day) => {
                      const dayIso = toIsoDate(day);
                      const slotBooking = bookingCoveringSlot(dayIso, slotIndex);
                      const booked = slotBooking !== null;
                      const selected = isSlotWithinSelection(dayIso, slotIndex);
                      const slotStart = slotStartMinutes(slotIndex, calendarStartHour);
                      const slotEnd = slotStart + SLOT_MINUTES;
                      const bookingStart = slotBooking ? timeToMinutes(slotBooking.time) : null;
                      const bookingEnd = slotBooking && bookingStart !== null
                        ? bookingStart + slotBooking.durationMinutes
                        : null;
                      const bookingStartsHere = slotBooking && bookingStart === slotStart;
                      const bookingEndsHere = slotBooking && bookingEnd === slotEnd;
                      const normalizedRegistration = slotBooking?.registrationNumber?.trim().toUpperCase() ?? '';
                      const hasEvaluations = normalizedRegistration
                        ? (evaluationLinksByRegistration[normalizedRegistration]?.some((item) => item.id !== null) ?? false)
                        : false;
                      const showEvaluationButton = bookingStartsHere && hasEvaluations && slotBooking.durationMinutes >= 30;

                      return (
                        <TableCell
                          key={`${dayIso}-${slotIndex}`}
                          data-testid={`calendar-cell-${dayIso}-${slotIndex}`}
                          onMouseDown={(event) => {
                            if (slotBooking) {
                              return;
                            }

                            event.preventDefault();
                            handleCellMouseDown(event, dayIso, slotIndex);
                          }}
                          onMouseEnter={(event) => handleCellMouseEnter(event, dayIso, slotIndex)}
                          onMouseUp={() => finalizeDrag()}
                          onClick={() => {
                            if (slotBooking) {
                              openBookingDialog(slotBooking);
                            }
                          }}
                          sx={{
                            p: 0,
                            cursor: booked ? 'pointer' : 'crosshair',
                            backgroundColor: selected ? '#38bdf8' : (booked ? '#1f4b7a' : '#0f172a'),
                            color: selected ? '#0b1220' : '#e2e8f0',
                            userSelect: 'none',
                            verticalAlign: 'middle',
                            border: '1px solid',
                            borderColor: '#1e293b',
                            height: slotHeight,
                            minHeight: slotHeight,
                            maxHeight: slotHeight,
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: booked ? '#1f4b7a' : '#38bdf8',
                              color: booked ? '#e2e8f0' : '#0b1220',
                            },
                            ...(booked && !selected ? {
                              boxShadow: [
                                'inset 2px 0 0 #7dd3fc',
                                'inset -2px 0 0 #7dd3fc',
                                bookingStartsHere ? 'inset 0 2px 0 #7dd3fc' : '',
                                bookingEndsHere ? 'inset 0 -2px 0 #7dd3fc' : '',
                              ].filter(Boolean).join(', '),
                            } : {}),
                          }}
                        >
                          {bookingStartsHere && slotBooking && (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: '1px 2px',
                                px: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  lineHeight: 1,
                                  fontWeight: 600,
                                  color: '#e5edf7',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                }}
                              >
                                {calendarBookingLabel(slotBooking)}
                              </Typography>
                              {showEvaluationButton && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(event) => openEvaluationMenu(event, slotBooking.registrationNumber)}
                                  aria-label={`Vis dekkvurderinger for ${slotBooking.registrationNumber}`}
                                  sx={{
                                    minWidth: 0,
                                    px: 0.75,
                                    py: 0,
                                    fontSize: 10,
                                    lineHeight: 1.2,
                                    borderColor: '#67e8f9',
                                    color: '#ecfeff',
                                    textTransform: 'none',
                                    bgcolor: 'rgba(8, 47, 73, 0.35)',
                                    '&:hover': {
                                      borderColor: '#a5f3fc',
                                      bgcolor: 'rgba(14, 116, 144, 0.45)',
                                    },
                                  }}
                                >
                                  Dekk
                                </Button>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Dialog open={bookingDialogOpen} onClose={closeBookingDialog} fullWidth maxWidth="sm">
          <DialogTitle>Bookingdetaljer</DialogTitle>
          <DialogContent>
            {activeBooking && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Status: {activeBooking.status === AppointmentStatus.CancelledByAdmin ? 'Avlyst' : 'Aktiv'}
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.unknownCustomer}
                      onChange={(event) => setEditForm((prev) => ({
                        ...prev,
                        unknownCustomer: event.target.checked,
                      }))}
                    />
                  }
                  label="Ukjent kunde (kundenavn, telefonnummer og registreringsnummer er valgfritt)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kundenavn"
                  value={editForm.customerName}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, customerName: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefonnummer"
                  value={editForm.phone}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, phone: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Registreringsnummer"
                  value={editForm.registrationNumber}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, registrationNumber: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Dato"
                  value={editForm.date}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, date: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="time"
                  label="Starttid"
                  value={editForm.startTime}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, startTime: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Varighet (min)"
                  value={editForm.durationMinutes}
                  inputProps={{ min: 1, step: 15 }}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    setEditForm((prev) => ({
                      ...prev,
                      durationMinutes: Number.isFinite(value) ? Math.max(1, value) : prev.durationMinutes,
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  label="Jobbtype"
                  value={editForm.jobType ?? ''}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, jobType: event.target.value as JobType | '' }))}
                >
                  <MenuItem value="">Ikke angitt</MenuItem>
                  {JOB_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{JOB_TYPE_LABELS[option]}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  required={jobTypeRequiresNote(editForm.jobType)}
                  error={jobTypeRequiresNote(editForm.jobType) && !editForm.note?.trim()}
                  helperText={jobTypeRequiresNote(editForm.jobType) && !editForm.note?.trim() ? noteRequiredMessage(editForm.jobType) : undefined}
                  label="Hva skal gjøres"
                  value={editForm.note}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, note: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Avbestillingsgrunn (valgfri)"
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeBookingDialog}>Lukk</Button>
            <Button
              color="error"
              variant="outlined"
              onClick={submitCancellation}
              disabled={dialogCancelling || dialogSaving || !activeBookingId}
            >
              {dialogCancelling ? 'Avlyser...' : 'Avlys booking'}
            </Button>
            <Button
              variant="contained"
              onClick={submitEditedBooking}
              disabled={dialogSaving || dialogCancelling || !activeBookingId}
            >
              {dialogSaving ? 'Lagrer...' : 'Lagre endringer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={evaluationMenuAnchorEl}
          open={isEvaluationMenuOpen}
          onClose={closeEvaluationMenu}
          keepMounted
        >
          <MenuItem disabled>
            <ListItemText
              primary={
                evaluationMenuRegistration
                  ? `Dekkvurderinger for ${evaluationMenuRegistration}`
                  : 'Dekkvurderinger'
              }
            />
          </MenuItem>

          {loadingEvaluationRegistration === evaluationMenuRegistration && (
            <MenuItem disabled>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <ListItemText primary="Laster dekkvurderinger..." />
            </MenuItem>
          )}

          {loadingEvaluationRegistration !== evaluationMenuRegistration && evaluationMenuError && (
            <MenuItem disabled>
              <ListItemText primary={evaluationMenuError} />
            </MenuItem>
          )}

          {loadingEvaluationRegistration !== evaluationMenuRegistration &&
            !evaluationMenuError &&
            evaluationLinksWithId.length === 0 && (
              <MenuItem disabled>
                <ListItemText primary="Ingen dekkvurderinger funnet." />
              </MenuItem>
            )}

          {loadingEvaluationRegistration !== evaluationMenuRegistration &&
            !evaluationMenuError &&
            evaluationLinksWithId.map((evaluation) => (
              <MenuItem
                key={`${evaluation.id}-${evaluation.season ?? 'season'}`}
                component={RouterLink}
                to={`/admin/auth/tirerating/${evaluation.id}`}
                onClick={closeEvaluationMenu}
              >
                <ListItemText
                  primary={`${evaluation.season ?? 'Ukjent sesong'}${evaluation.rating ? ` (${evaluation.rating})` : ''}`}
                  secondary={evaluation.performedBy ?? undefined}
                />
              </MenuItem>
            ))}
        </Menu>
      </Grid>
    </Grid>
  );
}

export default AdminManualBooking;
